import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { usePlans, DEFAULT_PLANS } from '../hooks/usePlans';

const companyName = process.env.REACT_APP_COMPANY_NAME || 'autopilot';
const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'support@autopilot.co.zw';

type FeatureVal = boolean | string;

interface FeatureRow {
    label: string;
    tooltip?: string;
    free: FeatureVal;
    starter: FeatureVal;
    business: FeatureVal;
    agency: FeatureVal;
}

interface FeatureGroup {
    group: string;
    rows: FeatureRow[];
}

const FEATURES: FeatureGroup[] = [
    {
        group: 'Bots & Channels',
        rows: [
            { label: 'Number of bots', free: '1', starter: '1', business: '3', agency: '10' },
            { label: 'Website chat widget', free: true, starter: true, business: true, agency: true },
            { label: 'WhatsApp bot', free: false, starter: true, business: true, agency: true },
            { label: 'Facebook Messenger bot', free: false, starter: true, business: true, agency: true },
            { label: 'Voice bot', free: false, starter: true, business: true, agency: true },
        ],
    },
    {
        group: 'Conversations & Usage',
        rows: [
            { label: 'Conversations / month', free: '100', starter: '500', business: '2,000', agency: '10,000' },
            { label: 'Messages per conversation', free: '20', starter: 'Unlimited', business: 'Unlimited', agency: 'Unlimited' },
            { label: 'Contacts stored', free: '200', starter: '1,000', business: '5,000', agency: 'Unlimited' },
        ],
    },
    {
        group: 'Features',
        rows: [
            { label: 'Pre-built industry templates', free: true, starter: true, business: true, agency: true },
            { label: 'Visual flow builder', free: false, starter: false, business: true, agency: true },
            { label: 'AI-powered smart replies', free: false, starter: false, business: true, agency: true },
            { label: 'Appointment booking', free: false, starter: true, business: true, agency: true },
            { label: 'Lead capture forms', free: false, starter: true, business: true, agency: true },
            { label: 'Broadcast / bulk messages', free: false, starter: false, business: true, agency: true },
            { label: 'Review collection flow', free: false, starter: true, business: true, agency: true },
        ],
    },
    {
        group: 'Campaigns',
        rows: [
            { label: 'Mass campaign messaging', free: false, starter: true, business: true, agency: true },
            { label: 'Active campaigns', free: '—', starter: '1', business: '3', agency: 'Unlimited' },
            { label: 'Send days per week', free: '—', starter: '1 day', business: '2 days', agency: 'Every day' },
            { label: 'Contact list max size', free: '—', starter: '500', business: '2,000', agency: 'Unlimited' },
            { label: 'Contact list opt-in notifications', free: false, starter: true, business: true, agency: true },
            { label: 'Unsubscribe handling (auto)', free: false, starter: true, business: true, agency: true },
            { label: 'CSV contact import / export', free: false, starter: true, business: true, agency: true },
            { label: 'Campaign stats & delivery reports', free: false, starter: true, business: true, agency: true },
        ],
    },
    {
        group: 'Analytics & Reporting',
        rows: [
            { label: 'Basic analytics', free: true, starter: true, business: true, agency: true },
            { label: 'Advanced analytics & reports', free: false, starter: false, business: true, agency: true },
            { label: 'Export conversation data', free: false, starter: false, business: true, agency: true },
        ],
    },
    {
        group: 'Integrations & API',
        rows: [
            { label: 'Webhook integrations', free: false, starter: false, business: true, agency: true },
            { label: 'REST API access', free: false, starter: false, business: true, agency: true },
            { label: 'Custom integrations', free: false, starter: false, business: false, agency: true },
        ],
    },
    {
        group: 'Branding & White Label',
        rows: [
            { label: 'Remove {companyName} branding', free: false, starter: false, business: false, agency: true },
            { label: 'White-label dashboard', free: false, starter: false, business: false, agency: true },
            { label: 'Custom domain', free: false, starter: false, business: false, agency: true },
        ],
    },
    {
        group: 'Support',
        rows: [
            { label: 'Community support', free: true, starter: true, business: true, agency: true },
            { label: 'Email support', free: false, starter: true, business: true, agency: true },
            { label: 'Priority support', free: false, starter: false, business: true, agency: true },
            { label: 'Dedicated account manager', free: false, starter: false, business: false, agency: true },
        ],
    },
];

function Check({ color }: { color: string }) {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="9" cy="9" r="9" fill={color} fillOpacity="0.15" />
            <path d="M5.5 9l2.5 2.5 4.5-5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function Cross() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="9" cy="9" r="9" fill="#1e293b" />
            <path d="M6 6l6 6M12 6l-6 6" stroke="#334155" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    );
}

export default function Pricing() {
    const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');
    const [openFaq, setOpenFaq] = useState<number | null>(null);
    const { plans: livePlans, setupFees } = usePlans();

    // Map server plans to the display shape this page expects
    const PLANS = livePlans
        .filter(p => !p.contactOnly)
        .map(p => ({
            id: p.id,
            name: p.name,
            icon: p.icon,
            monthlyPrice: p.monthly ?? 0,
            yearlyPrice:  p.yearly  ?? 0,
            desc: p.description,
            color: p.color,
            popular: p.popular,
            cta: p.id === 'free' ? 'Start free' : p.id === 'business' ? 'Start 7-Day Free Trial' : 'Get started',
            ctaLink: '/register',
            trial: p.id === 'business',
        }));
    const enterprisePlan = livePlans.find(p => p.id === 'enterprise');

    const faqs = [
        {
            q: 'What is the setup fee and why is it charged?',
            a: `The one-time setup fee covers the work we do to get your bot live: configuring your WhatsApp Business number with Meta, building out your initial conversation flows from a template, connecting your channels, and testing everything before you go live. It's paid once — never again, even if you upgrade your plan.`,
        },
        {
            q: 'Do I pay the setup fee every time I add a new bot?',
            a: 'Yes, each new bot incurs its own setup fee because each one requires individual configuration and WhatsApp/Facebook registration. Your monthly subscription covers all your bots under that plan — only new bots added later carry a new setup fee.',
        },
        {
            q: 'Can I switch plans at any time?',
            a: "Yes. Upgrades take effect immediately and you're only charged the difference. Downgrades take effect at the end of your current billing period. There's no penalty for switching.",
        },
        {
            q: 'What happens if I exceed my chat allowance?',
            a: "Your bot never pauses or goes silent. On paid plans, anything over your monthly chat allowance is metered at a published rate (currently $0.05/extra chat, $0.10/extra AI chat) and added as a single line item on your next invoice — never a surprise mid-month charge. The Free plan has no overage option, so it pauses at its limit until the next month or an upgrade.",
        },
        {
            q: 'If I connect my own WhatsApp number, do I pay Meta separately?',
            a: "Yes. Connecting your own WhatsApp number (available on Business plan and above) creates a WhatsApp Business Account under your own Meta Business Manager, and Meta bills you directly for conversation costs on that account — the same as it would if you used WhatsApp's API without us. That's separate from, and in addition to, your Autopilot subscription, which covers the bot-building platform itself. On Starter and Free, you use our shared WhatsApp number instead, and we cover that cost — no Meta account or billing needed on your end.",
        },
        {
            q: 'Is there a free trial?',
            a: "Yes — the Business plan comes with a 7-day free trial. You enter your card details upfront, but you're not charged until day 7. Cancel any time before then and you pay nothing. The Free plan is also yours forever with no timer — 1 bot and 100 chats per month, no card required.",
        },
        {
            q: 'What payment methods do you accept?',
            a: 'We accept Visa / Mastercard via Stripe, EcoCash, OneMoney, and PayPal. All payments are processed securely — we never store your card details.',
        },
    ];

    return (
        <>
            <SEO
                title={`Pricing | ${companyName}`}
                description="Simple, transparent pricing for WhatsApp, website, and Facebook bots. No hidden fees."
                keywords="pricing, chatbot plans, WhatsApp bot cost, Zimbabwe"
                type="website"
            />

            <style>{`
        * { box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        body { margin: 0; }

        .pricing-hero { padding: 120px 24px 64px; }
        .pricing-cards { grid-template-columns: repeat(4, 1fr); }
        .comparison-table { display: block; }
        .mobile-comparison { display: none; }

        @media (max-width: 1100px) {
          .pricing-cards { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 700px) {
          .pricing-hero { padding: 90px 20px 48px; }
          .pricing-cards { grid-template-columns: 1fr; max-width: 400px; margin-left: auto; margin-right: auto; }
          .comparison-table { display: none; }
          .mobile-comparison { display: block; }
          .section-wrap { padding: 48px 20px !important; }
          h1 { font-size: 32px !important; }
          h2 { font-size: 26px !important; }
        }
      `}</style>

            <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: '#0a0914', minHeight: '100vh', color: 'white' }}>

                {/* ── NAV ── */}
                <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, padding: '0 32px', height: '60px', background: 'rgba(10,9,20,0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Link to="/"><img src="/Logo-white.png" alt={companyName} style={{ height: '68px', display: 'block', objectFit: 'contain' }} /></Link>
                    <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                        <Link to="/" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>Home</Link>
                        <Link to="/about" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>About</Link>
                        <Link to="/pricing" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>Pricing</Link>
                        <Link to="/documentation" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>Docs</Link>
                        <Link to="/contact" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>Contact</Link>
                        <Link to="/register" style={{ background: '#5B4FE8', color: 'white', padding: '8px 20px', borderRadius: '20px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>Get started</Link>
                    </div>
                </nav>

                {/* ── HERO ── */}
                <div className="pricing-hero" style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
                    <div style={{ display: 'inline-block', background: 'rgba(91,79,232,0.18)', border: '1px solid rgba(91,79,232,0.35)', color: '#a5b4fc', fontSize: '11px', fontWeight: '700', padding: '4px 14px', borderRadius: '20px', letterSpacing: '0.08em', marginBottom: '20px' }}>
                        TRANSPARENT PRICING
                    </div>
                    <h1 style={{ fontSize: '52px', fontWeight: '800', letterSpacing: '-0.03em', margin: '0 0 16px', lineHeight: 1.1 }}>
                        Simple pricing,<br />
                        <span style={{ color: '#818cf8' }}>no surprises</span>
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '17px', lineHeight: 1.7, margin: '0 0 20px' }}>
                        One monthly fee covers your bots and conversations. A one-time setup fee gets you live. That's it.
                    </p>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '30px', padding: '8px 18px', marginBottom: '24px', fontSize: '13px', color: '#34d399', fontWeight: '600' }}>
                        🎁 Business plan includes a <strong style={{ color: '#10b981' }}>7-day free trial</strong> — card required, cancel anytime
                    </div>

                    {/* Toggle */}
                    <div style={{ display: 'inline-flex', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '30px', padding: '4px', gap: '4px' }}>
                        {(['monthly', 'yearly'] as const).map(b => (
                            <button key={b} onClick={() => setBilling(b)} style={{ padding: '9px 24px', borderRadius: '26px', border: 'none', cursor: 'pointer', background: billing === b ? '#5B4FE8' : 'transparent', color: billing === b ? 'white' : 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s', position: 'relative', fontFamily: 'inherit' }}>
                                {b === 'monthly' ? 'Monthly' : 'Yearly'}
                                {b === 'yearly' && (
                                    <span style={{ position: 'absolute', top: '-10px', right: '-10px', background: '#10b981', color: 'white', fontSize: '9px', fontWeight: '800', padding: '2px 6px', borderRadius: '10px' }}>-20%</span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── PLAN CARDS ── */}
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 64px' }}>
                    <div className="pricing-cards" style={{ display: 'grid', gap: '16px' }}>
                        {PLANS.map(plan => {
                            const price = billing === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
                            const perMonth = billing === 'yearly' && plan.yearlyPrice > 0 ? Math.round(plan.yearlyPrice / 12) : price;
                            return (
                                <div key={plan.id} style={{ background: plan.popular ? 'linear-gradient(160deg,#2d1b69 0%,#1a0f4a 100%)' : 'rgba(255,255,255,0.04)', border: plan.popular ? '2px solid #5B4FE8' : '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '28px 24px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                                    {plan.popular && (
                                        <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: '#5B4FE8', color: 'white', fontSize: '10px', fontWeight: '800', padding: '4px 14px', borderRadius: '20px', whiteSpace: 'nowrap', letterSpacing: '0.06em' }}>
                                            MOST POPULAR
                                        </div>
                                    )}
                                    {(plan as any).trial && (
                                        <div style={{ position: 'absolute', top: '16px', right: '-4px', background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', fontSize: '9px', fontWeight: '800', padding: '4px 10px 4px 12px', borderRadius: '20px 0 0 20px', letterSpacing: '0.06em', boxShadow: '0 2px 8px rgba(16,185,129,0.4)' }}>
                                            🎁 7-DAY FREE TRIAL
                                        </div>
                                    )}
                                    <div style={{ fontSize: '28px', marginBottom: '10px' }}>{plan.icon}</div>
                                    <div style={{ fontWeight: '800', fontSize: '20px', marginBottom: '4px' }}>{plan.name}</div>
                                    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px', marginBottom: '20px' }}>{plan.desc}</div>

                                    <div style={{ marginBottom: '6px' }}>
                                        <span style={{ fontSize: '40px', fontWeight: '800', letterSpacing: '-0.02em' }}>${billing === 'yearly' && price > 0 ? perMonth : price}</span>
                                        {price > 0 && <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>/mo{billing === 'yearly' ? '*' : ''}</span>}
                                    </div>
                                    {billing === 'yearly' && price > 0 && (
                                        <div style={{ fontSize: '12px', color: '#10b981', marginBottom: '4px' }}>
                                            Billed as ${plan.yearlyPrice}/year · save ${(plan.monthlyPrice * 12) - plan.yearlyPrice}/yr
                                        </div>
                                    )}
                                    <div style={{ height: '22px' }} />

                                    <Link to={plan.ctaLink} style={{ display: 'block', padding: '12px', background: plan.popular ? '#5B4FE8' : 'rgba(255,255,255,0.08)', color: 'white', textAlign: 'center', borderRadius: '12px', textDecoration: 'none', fontWeight: '700', fontSize: '14px', marginTop: 'auto', border: plan.popular ? 'none' : '1px solid rgba(255,255,255,0.12)', transition: 'opacity 0.2s' }}>
                                        {plan.cta}
                                    </Link>
                                    {(plan as any).trial && (
                                        <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                                            Card required · Cancel before day 7 to avoid charge
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* ── Enterprise card ── */}
                        {enterprisePlan && (
                            <div style={{ background: 'linear-gradient(160deg,#1a1409,#120e03)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '20px', padding: '28px 24px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: '#f59e0b', color: '#000', fontSize: '10px', fontWeight: '800', padding: '4px 14px', borderRadius: '20px', whiteSpace: 'nowrap', letterSpacing: '0.06em' }}>
                                    CUSTOM
                                </div>
                                <div style={{ fontSize: '28px', marginBottom: '10px' }}>{enterprisePlan.icon}</div>
                                <div style={{ fontWeight: '800', fontSize: '20px', marginBottom: '4px' }}>{enterprisePlan.name}</div>
                                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px', marginBottom: '20px' }}>{enterprisePlan.description}</div>
                                <div style={{ marginBottom: '6px' }}>
                                    <span style={{ color: '#fbbf24', fontSize: '28px', fontWeight: '800' }}>Custom pricing</span>
                                </div>
                                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginBottom: '22px' }}>Negotiated to fit your scale</div>
                                <Link to="/contact" style={{ display: 'block', padding: '12px', background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: '#000', textAlign: 'center', borderRadius: '12px', textDecoration: 'none', fontWeight: '700', fontSize: '14px', marginTop: 'auto', transition: 'opacity 0.2s' }}>
                                    Contact Sales
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Channel setup fee note */}
                    <div style={{ maxWidth: '1200px', margin: '20px auto 0', padding: '0 24px' }}>
                        <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)', borderRadius: '12px', padding: '14px 20px', fontSize: '13px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>
                            ⚡ <strong style={{ color: '#fbbf24' }}>One-time channel setup fees</strong> apply to managed channels only:{' '}
                            <strong style={{ color: 'rgba(255,255,255,0.7)' }}>WhatsApp (own number) — ${setupFees.whatsapp?.own_number ?? 20}</strong>,{' '}
                            <strong style={{ color: 'rgba(255,255,255,0.7)' }}>Facebook Messenger (managed setup) — ${setupFees.facebook?.we_setup ?? 50}</strong>.{' '}
                            Connecting via Meta/Facebook OAuth or bringing your own API credentials has no setup fee. Fees are charged once per bot, not per plan renewal.
                        </div>
                    </div>
                </div>

                {/* ── SETUP FEE EXPLAINER ── */}
                <div className="section-wrap" style={{ padding: '64px 24px', background: 'rgba(245,158,11,0.04)', borderTop: '1px solid rgba(245,158,11,0.1)', borderBottom: '1px solid rgba(245,158,11,0.1)' }}>
                    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '32px', alignItems: 'start' }}>
                            <div style={{ fontSize: '48px', lineHeight: 1 }}>⚡</div>
                            <div>
                                <h2 style={{ fontSize: '26px', fontWeight: '800', margin: '0 0 10px', letterSpacing: '-0.02em' }}>
                                    How the setup fee works
                                </h2>
                                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '15px', lineHeight: 1.7, margin: '0 0 24px' }}>
                                    Every bot requires a one-time setup fee — charged once, never again. Here's what it covers:
                                </p>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                                    {[
                                        { icon: '📱', title: 'WhatsApp registration', desc: 'We submit your number to Meta and handle the Business API approval process.' },
                                        { icon: '🔧', title: 'Bot configuration', desc: 'We build your initial flows from a template tailored to your industry.' },
                                        { icon: '🔗', title: 'Channel connection', desc: 'Website widget code, Facebook page linking, or number porting — done for you.' },
                                        { icon: '✅', title: 'Testing & launch', desc: 'We run end-to-end tests and only hand over when everything works perfectly.' },
                                    ].map((item, i) => (
                                        <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '16px' }}>
                                            <div style={{ fontSize: '22px', marginBottom: '8px' }}>{item.icon}</div>
                                            <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '5px' }}>{item.title}</div>
                                            <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', lineHeight: 1.6 }}>{item.desc}</div>
                                        </div>
                                    ))}
                                </div>
                                <div style={{ marginTop: '20px', padding: '14px 18px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '10px', fontSize: '13px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                                    <strong style={{ color: '#fbbf24' }}>Note:</strong> The setup fee applies per bot, not per account. Your monthly subscription covers all bots on your plan. If you add a new bot later, only that bot's setup fee applies.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── COMPARISON TABLE (desktop) ── */}
                <div className="comparison-table section-wrap" style={{ padding: '80px 24px' }}>
                    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                            <h2 style={{ fontSize: '34px', fontWeight: '800', letterSpacing: '-0.02em', margin: '0 0 10px' }}>Full feature comparison</h2>
                            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '15px' }}>Everything side-by-side so you can choose with confidence</p>
                        </div>

                        <div style={{ overflowX: 'auto', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                        <th style={{ padding: '20px 24px', textAlign: 'left', width: '34%', color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Feature</th>
                                        {PLANS.map(p => (
                                            <th key={p.id} style={{ padding: '20px 16px', textAlign: 'center', background: p.popular ? 'rgba(91,79,232,0.08)' : 'transparent' }}>
                                                <div style={{ fontWeight: '800', fontSize: '15px', color: p.popular ? '#818cf8' : 'white', marginBottom: '3px' }}>{p.name}</div>
                                                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>
                                                    {billing === 'monthly'
                                                        ? p.monthlyPrice === 0 ? 'Free' : `$${p.monthlyPrice}/mo`
                                                        : p.yearlyPrice === 0 ? 'Free' : `$${Math.round(p.yearlyPrice / 12)}/mo`}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {FEATURES.map((group, gi) => (
                                        <React.Fragment key={gi}>
                                            {/* Group header */}
                                            <tr>
                                                <td colSpan={5} style={{ padding: '18px 24px 8px', background: 'rgba(255,255,255,0.02)', fontSize: '11px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5B4FE8' }}>
                                                    {group.group}
                                                </td>
                                            </tr>
                                            {group.rows.map((row, ri) => (
                                                <tr key={ri} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                                                    <td style={{ padding: '13px 24px', fontSize: '14px', color: 'rgba(255,255,255,0.75)' }}>
                                                        {row.label.replace('{companyName}', companyName)}
                                                    </td>
                                                    {(['free', 'starter', 'business', 'agency'] as const).map(pid => {
                                                        const val = row[pid];
                                                        const plan = PLANS.find(p => p.id === pid)!;
                                                        return (
                                                            <td key={pid} style={{ padding: '13px 16px', textAlign: 'center', background: plan.popular ? 'rgba(91,79,232,0.04)' : 'transparent' }}>
                                                                {typeof val === 'boolean' ? (
                                                                    val ? <div style={{ display: 'flex', justifyContent: 'center' }}><Check color={plan.color} /></div> : <div style={{ display: 'flex', justifyContent: 'center' }}><Cross /></div>
                                                                ) : (
                                                                    <span style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.8)' }}>{val}</span>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                    {/* Setup fee row */}
                                    <tr style={{ borderTop: '2px solid rgba(255,255,255,0.1)', background: 'rgba(245,158,11,0.03)' }}>
                                        <td style={{ padding: '16px 24px', fontSize: '14px', fontWeight: '700', color: '#fbbf24' }}>
                                            One-time setup fee
                                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', fontWeight: '400', marginTop: '2px' }}>Per managed channel · charged once</div>
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'center' }}>
                                            <span style={{ fontWeight: '700', fontSize: '13px', color: '#34d399' }}>Free</span>
                                        </td>
                                        {PLANS.slice(1).map(p => (
                                            <td key={p.id} style={{ padding: '16px', textAlign: 'center', background: p.popular ? 'rgba(91,79,232,0.04)' : 'transparent' }}>
                                                <span style={{ fontWeight: '600', fontSize: '12px', color: '#fbbf24' }}>
                                                    WhatsApp ${setupFees.whatsapp?.own_number ?? 20} · FB ${setupFees.facebook?.we_setup ?? 50}
                                                </span>
                                            </td>
                                        ))}
                                    </tr>
                                    {/* CTA row */}
                                    <tr style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                        <td style={{ padding: '20px 24px' }} />
                                        {PLANS.map(p => (
                                            <td key={p.id} style={{ padding: '20px 16px', textAlign: 'center', background: p.popular ? 'rgba(91,79,232,0.04)' : 'transparent' }}>
                                                <Link to={p.ctaLink} style={{ display: 'inline-block', padding: '10px 20px', background: p.popular ? '#5B4FE8' : 'rgba(255,255,255,0.08)', color: 'white', borderRadius: '10px', textDecoration: 'none', fontSize: '13px', fontWeight: '700', border: p.popular ? 'none' : '1px solid rgba(255,255,255,0.12)' }}>
                                                    {p.cta}
                                                </Link>
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* ── MOBILE COMPARISON (accordion per plan) ── */}
                <div className="mobile-comparison section-wrap" style={{ padding: '48px 20px' }}>
                    <h2 style={{ fontSize: '26px', fontWeight: '800', textAlign: 'center', marginBottom: '28px', letterSpacing: '-0.02em' }}>Compare plans</h2>
                    {PLANS.map(plan => (
                        <details key={plan.id} style={{ marginBottom: '12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', overflow: 'hidden' }}>
                            <summary style={{ padding: '16px 20px', cursor: 'pointer', fontWeight: '700', fontSize: '15px', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{plan.icon} {plan.name}</span>
                                <span style={{ color: 'rgba(255,255,255,0.45)', fontWeight: '400', fontSize: '14px' }}>
                                    {billing === 'monthly' ? (plan.monthlyPrice === 0 ? 'Free' : `$${plan.monthlyPrice}/mo`) : (plan.yearlyPrice === 0 ? 'Free' : `$${Math.round(plan.yearlyPrice / 12)}/mo`)}
                                </span>
                            </summary>
                            <div style={{ padding: '0 20px 20px' }}>
                                {FEATURES.map((group, gi) => (
                                    <div key={gi} style={{ marginBottom: '16px' }}>
                                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#5B4FE8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px' }}>{group.group}</div>
                                        {group.rows.map((row, ri) => {
                                            const val = row[plan.id as keyof typeof row] as FeatureVal;
                                            return (
                                                <div key={ri} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: '13px' }}>
                                                    <span style={{ color: 'rgba(255,255,255,0.65)' }}>{row.label}</span>
                                                    {typeof val === 'boolean' ? (val ? <Check color={plan.color} /> : <Cross />) : <span style={{ fontWeight: '600' }}>{val}</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                                <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '8px', fontSize: '13px' }}>
                                    <span style={{ color: '#fbbf24', fontWeight: '700' }}>Setup fee: </span>
                                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                                        {plan.id === 'free'
                                            ? 'Free (website bot only)'
                                            : `WhatsApp $${setupFees.whatsapp?.own_number ?? 20} · Facebook $${setupFees.facebook?.we_setup ?? 50} · one-time per bot`}
                                    </span>
                                </div>
                            </div>
                        </details>
                    ))}
                </div>

                {/* ── FAQ ── */}
                <div className="section-wrap" style={{ padding: '64px 24px', background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ maxWidth: '680px', margin: '0 auto' }}>
                        <h2 style={{ fontSize: '30px', fontWeight: '800', textAlign: 'center', marginBottom: '36px', letterSpacing: '-0.02em' }}>Pricing FAQ</h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {faqs.map((faq, i) => (
                                <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', overflow: 'hidden' }}>
                                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ width: '100%', padding: '17px 20px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left', fontFamily: 'inherit', gap: '12px' }}>
                                        <span style={{ color: 'white', fontWeight: '600', fontSize: '14px', lineHeight: 1.4, flex: 1 }}>{faq.q}</span>
                                        <span style={{ color: '#5B4FE8', fontSize: '18px', flexShrink: 0, display: 'inline-block', transition: 'transform 0.2s', transform: openFaq === i ? 'rotate(45deg)' : 'none' }}>+</span>
                                    </button>
                                    {openFaq === i && (
                                        <div style={{ padding: '0 20px 18px', color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: 1.7 }}>
                                            {faq.a}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── CTA ── */}
                <div className="section-wrap" style={{ padding: '80px 24px', textAlign: 'center', background: 'linear-gradient(135deg,#1a1040 0%,#0c0820 100%)' }}>
                    <div style={{ fontSize: '42px', marginBottom: '14px' }}>🚀</div>
                    <h2 style={{ fontSize: '32px', fontWeight: '800', margin: '0 0 12px', letterSpacing: '-0.02em' }}>Ready to put your business on autopilot?</h2>
                    <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '16px', maxWidth: '460px', margin: '0 auto 28px', lineHeight: 1.6 }}>
                        Start free. Upgrade whenever you're ready. No tech skills needed.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Link to="/register" style={{ background: '#5B4FE8', color: 'white', padding: '14px 32px', borderRadius: '30px', textDecoration: 'none', fontSize: '15px', fontWeight: '700', boxShadow: '0 0 40px rgba(91,79,232,0.35)' }}>
                            Get started free →
                        </Link>
                        <a href={`mailto:${companyEmail}`} style={{ background: 'rgba(255,255,255,0.08)', color: 'white', padding: '14px 32px', borderRadius: '30px', textDecoration: 'none', fontSize: '15px', fontWeight: '600', border: '1px solid rgba(255,255,255,0.12)' }}>
                            Talk to us
                        </a>
                    </div>
                    <div style={{ marginTop: '14px', fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>
                        No credit card required · Cancel any time
                    </div>
                </div>

            </div>
        </>
    );
}